import { useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { useLogger } from '@/lib/logger';

interface UseCostOfGoodsOptions {
  /** False skips the read, e.g. for a caller without skus.costs_manage. */
  enabled?: boolean;
}

export function useCostOfGoods({ enabled = true }: UseCostOfGoodsOptions = {}) {
  const logger = useLogger();
  const utils = trpc.useUtils();

  const { data, isLoading, error } = trpc.inventory.getCostOfGoods.useQuery({}, { enabled });

  const upsertCostOfGoods = trpc.inventory.upsertCostOfGoods.useMutation({
    onSuccess: () => {
      void utils.inventory.getCostOfGoods.invalidate();
      toast({
        title: 'Costs updated',
        description: 'Cost of goods saved successfully.',
      });
    },
    onError: (err) => {
      toast({
        title: 'Update failed',
        description: err instanceof Error ? err.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  const deleteCostOfGoods = trpc.inventory.deleteCostOfGoods.useMutation({
    onSuccess: () => {
      void utils.inventory.getCostOfGoods.invalidate();
      toast({
        title: 'Orphaned entries removed',
        description: 'Stale cost entries have been deleted.',
      });
    },
    onError: (err) => {
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    if (!enabled || !error) return;
    logger.error('Failed to load cost of goods', {
      error: error instanceof Error ? error.message : String(error),
    });
    toast({
      title: 'Error',
      description: 'Failed to load cost of goods',
      variant: 'destructive',
    });
  }, [enabled, error, logger]);

  return {
    items: data ?? [],
    loading: isLoading,
    error,
    upsertCostOfGoods,
    deleteCostOfGoods,
  };
}
